import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { CartLine, computeTotals, formatCents, retailTotals } from '../money';
import { COLORS } from '../theme';

/** Guard window after the dialog opens during which the confirm button
 *  stays disabled. Same reasoning as the employee dialog: a double-tap
 *  on the purchase button would otherwise open and dismiss this in one
 *  gesture. */
const ARM_DELAY_MS = 800;

type Props = {
  open: boolean;
  lines: CartLine[];
  onCancel: () => void;
  onConfirm: () => void;
};

/** Confirmation for an owner withdrawal.
 *
 *  Louder than the employee dialog because it's doing something
 *  stranger: stock leaves the shelf, no money arrives, and the whole
 *  thing stays out of the day's takings. Everything it says is the
 *  thing someone would want to have been told if it was rung up by
 *  mistake. */
const OwnerConfirmDialog: React.FC<Props> = ({
  open,
  lines,
  onCancel,
  onConfirm,
}) => {
  const [armed, setArmed] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      setArmed(false);
      return;
    }
    const t = setTimeout(() => setArmed(true), ARM_DELAY_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Cancel owns the focus, so a stray Enter backs out rather than
  // writing off stock.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  const owner = computeTotals(lines, 'owner');
  const retail = retailTotals(lines);
  const units = lines.reduce((n, l) => n + l.qty, 0);

  return (
    <Dialog
      open={open}
      onClose={() => {
        /* backdrop does nothing — this needs a real answer */
      }}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ backgroundColor: COLORS.danger, color: '#fff', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 34 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem' }}>
            OWNER WITHDRAWAL
          </Typography>
        </Box>
        <Typography sx={{ opacity: 0.95 }}>
          This is not a sale. No money will be taken, but HST is recorded
          on the cost.
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Row
          label={`${units} ${units === 1 ? 'item' : 'items'} at cost`}
          value={formatCents(owner.subtotal)}
        />
        <Row label="HST recorded" value={formatCents(owner.tax)} />
        <Row label="To pay now" value={formatCents(0)} emphasise />
        <Row
          label="Retail value leaving the shelf"
          value={formatCents(retail.total)}
          muted
        />

        <Typography
          variant="body2"
          sx={{ color: COLORS.textMuted, mt: 2, mb: 1 }}
        >
          Stock will be reduced for every line, exactly as it would on a
          sale. The withdrawal is recorded and excluded from the day's
          takings.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          {/* Unequal weights: cancel is the easy, wide, default action. */}
          <Button
            ref={cancelRef}
            variant="contained"
            onClick={onCancel}
            sx={{ flex: 2, py: 2, fontSize: '1.1rem', fontWeight: 700 }}
          >
            CANCEL
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={!armed}
            onClick={onConfirm}
            sx={{ flex: 1, py: 2, fontWeight: 700 }}
          >
            WITHDRAW
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const Row: React.FC<{
  label: string;
  value: string;
  emphasise?: boolean;
  muted?: boolean;
}> = ({ label, value, emphasise, muted }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography sx={{ color: muted ? COLORS.textMuted : 'inherit' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: emphasise ? 800 : 600,
        fontSize: emphasise ? '1.3rem' : '1rem',
        color: muted ? COLORS.textMuted : emphasise ? COLORS.danger : 'inherit',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default OwnerConfirmDialog;
